/**
 * Top-level Organisation Management hook composing data, state, and handlers.
 *
 * Extracted from OrganisationManagementPage.tsx (Sub-PR 6.3).
 */

import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { Organisation, XeroInvoiceState } from '../../types'
import type { CreateOrgForm, OrphanedXeroOrgId } from './types'
import { useOrganisationData } from './use-organisation-data'
import { useOrganisationHandlers } from './use-organisation-handlers'

const EMPTY_FORM: CreateOrgForm = {
  name: '',
  slug: '',
  orgType: 'customer',
  xeroOrganisationId: '',
}

interface UseOrganisationManagementResult {
  organisations: Organisation[]
  xeroInvoices: XeroInvoiceState[]
  loading: boolean
  error: string | null
  setError: (msg: string | null) => void
  orphanedXeroOrgIds: OrphanedXeroOrgId[]
  searchQuery: string
  setSearchQuery: (q: string) => void
  showCreateModal: boolean
  setShowCreateModal: (v: boolean) => void
  showEditModal: boolean
  setShowEditModal: (v: boolean) => void
  showDeleteModal: boolean
  setShowDeleteModal: (v: boolean) => void
  selectedOrg: Organisation | null
  setSelectedOrg: (org: Organisation | null) => void
  actionMenuOrg: string | null
  setActionMenuOrg: (id: string | null) => void
  formData: CreateOrgForm
  setFormData: (data: CreateOrgForm) => void
  formLoading: boolean
  formError: string | null
  setFormError: (msg: string | null) => void
  resetForm: () => void
  handleCreateOrg: (e: React.FormEvent) => Promise<void>
  handleUpdateOrg: (e: React.FormEvent) => Promise<void>
  handleDeleteOrg: () => Promise<void>
  handleToggleStatus: (org: Organisation) => Promise<void>
  openEditModal: (org: Organisation) => void
  openDeleteModal: (org: Organisation) => void
}

function useOrganisationPageState(): {
  searchQuery: string
  setSearchQuery: (q: string) => void
  showCreateModal: boolean
  setShowCreateModal: (v: boolean) => void
  showEditModal: boolean
  setShowEditModal: (v: boolean) => void
  showDeleteModal: boolean
  setShowDeleteModal: (v: boolean) => void
  selectedOrg: Organisation | null
  setSelectedOrg: (o: Organisation | null) => void
  actionMenuOrg: string | null
  setActionMenuOrg: (id: string | null) => void
  formData: CreateOrgForm
  setFormData: (d: CreateOrgForm) => void
  formLoading: boolean
  setFormLoading: (v: boolean) => void
  formError: string | null
  setFormError: (m: string | null) => void
} {
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<Organisation | null>(null)
  const [actionMenuOrg, setActionMenuOrg] = useState<string | null>(null)
  const [formData, setFormData] = useState<CreateOrgForm>(EMPTY_FORM)
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  return {
    searchQuery,
    setSearchQuery,
    showCreateModal,
    setShowCreateModal,
    showEditModal,
    setShowEditModal,
    showDeleteModal,
    setShowDeleteModal,
    selectedOrg,
    setSelectedOrg,
    actionMenuOrg,
    setActionMenuOrg,
    formData,
    setFormData,
    formLoading,
    setFormLoading,
    formError,
    setFormError,
  }
}

function usePrefillFromQuery(
  setFormData: (d: CreateOrgForm) => void,
  setShowCreateModal: (v: boolean) => void,
  formData: CreateOrgForm
): void {
  const [searchParams, setSearchParams] = useSearchParams()
  const formDataRef = useRef(formData)
  formDataRef.current = formData
  const setFormDataRef = useRef(setFormData)
  setFormDataRef.current = setFormData
  const setShowCreateModalRef = useRef(setShowCreateModal)
  setShowCreateModalRef.current = setShowCreateModal
  useEffect(() => {
    const xeroOrgId = searchParams.get('xeroOrgId')
    if (!xeroOrgId) return
    setFormDataRef.current({ ...formDataRef.current, xeroOrganisationId: xeroOrgId })
    setShowCreateModalRef.current(true)
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])
}

export function useOrganisationManagement(): UseOrganisationManagementResult {
  const data = useOrganisationData()
  const state = useOrganisationPageState()

  usePrefillFromQuery(state.setFormData, state.setShowCreateModal, state.formData)

  const resetForm = (): void => {
    state.setFormData(EMPTY_FORM)
    state.setFormError(null)
  }

  const handlers = useOrganisationHandlers({
    selectedOrg: state.selectedOrg,
    formData: state.formData,
    setFormLoading: state.setFormLoading,
    setFormError: state.setFormError,
    setShowCreateModal: state.setShowCreateModal,
    setShowEditModal: state.setShowEditModal,
    setShowDeleteModal: state.setShowDeleteModal,
    setSelectedOrg: state.setSelectedOrg,
    setActionMenuOrg: state.setActionMenuOrg,
    resetForm,
    loadData: data.loadData,
  })

  const openEditModal = (org: Organisation): void => {
    state.setSelectedOrg(org)
    state.setFormData({
      name: org.name,
      slug: org.slug,
      orgType: org.orgType,
      xeroOrganisationId: org.xeroOrganisationId ?? '',
    })
    state.setShowEditModal(true)
    state.setActionMenuOrg(null)
  }

  const openDeleteModal = (org: Organisation): void => {
    state.setSelectedOrg(org)
    state.setShowDeleteModal(true)
    state.setActionMenuOrg(null)
  }

  return {
    organisations: data.organisations,
    xeroInvoices: data.xeroInvoices,
    loading: data.loading,
    error: data.error,
    setError: data.setError,
    orphanedXeroOrgIds: data.orphanedXeroOrgIds,
    ...state,
    resetForm,
    ...handlers,
    openEditModal,
    openDeleteModal,
  }
}
